import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Switch } from "../ui/switch";
import { RESTAURANTS, ROLES, ROLE_LABEL } from "../../constants";
import UserAreaPicker from "./UserAreaPicker";
import { buildUserPayload, validateUserForm } from "./userFormUtils";

const EMPTY_USER = {
  name: "",
  username: "",
  email: "",
  role: ROLES.EMPLOYEE,
  areas: ["kitchen"],
  restaurant: "restaurant_1",
  pin: "",
  active: true,
};

function initialFormFor(user) {
  if (!user) return EMPTY_USER;
  const areas = Array.isArray(user.areas) ? user.areas : user.area ? [user.area] : [];
  const username = user.username || user.email?.split("@")[0] || "";
  return { ...EMPTY_USER, ...user, username, areas };
}

export default function UserDialog({ open, onOpenChange, user, onSave }) {
  const [form, setForm] = useState(EMPTY_USER);

  useEffect(() => {
    if (open) setForm(initialFormFor(user));
  }, [open, user]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleArea = (id) => setForm((f) => {
    const cur = f.areas || [];
    return { ...f, areas: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
  });

  const submit = async () => {
    const error = validateUserForm(form);
    if (error) return toast.error(error);
    try {
      const payload = await buildUserPayload(form, user);
      onSave(payload, !!user);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message || "Could not save user");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-[#FBF8F1] border-[#E6E0D4]">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-[#0B1F3A]">
            {user ? "Edit user" : "New user"}
          </DialogTitle>
          <DialogDescription className="text-[#0B1F3A]/65">
            Pick a role and where they work. Employees only see products assigned to any of their areas.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name">
              <Input
                value={form.name}
                onChange={(e) => set({ name: e.target.value })}
                className="mt-1 bg-white border-[#E6E0D4]"
              />
            </Field>
            <Field label="Username">
              <Input
                value={form.username}
                onChange={(e) => set({ username: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, "") })}
                autoCapitalize="none"
                placeholder="first.last"
                className="mt-1 bg-white border-[#E6E0D4]"
              />
            </Field>
          </div>

          <Field label="Email">
              <Input
                value={form.email}
                onChange={(e) => set({ email: e.target.value })}
                placeholder="name@domain.com"
                className="mt-1 bg-white border-[#E6E0D4]"
              />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Role">
              <Select value={form.role} onValueChange={(v) => set({ role: v })}>
                <SelectTrigger className="mt-1 bg-white border-[#E6E0D4]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ROLES.MAIN}>{ROLE_LABEL[ROLES.MAIN]}</SelectItem>
                  <SelectItem value={ROLES.RESTAURANT}>{ROLE_LABEL[ROLES.RESTAURANT]}</SelectItem>
                  <SelectItem value={ROLES.EMPLOYEE}>{ROLE_LABEL[ROLES.EMPLOYEE]}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="PIN">
              <Input
                value={form.pin}
                onChange={(e) => set({ pin: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                inputMode="numeric"
                placeholder={user ? "•••• (leave blank to keep)" : "4 digits"}
                className="mt-1 bg-white border-[#E6E0D4] tracking-[0.4em]"
              />
            </Field>
          </div>

          {form.role !== ROLES.MAIN && (
            <Field label="Restaurant">
              <Select value={form.restaurant} onValueChange={(v) => set({ restaurant: v })}>
                <SelectTrigger className="mt-1 bg-white border-[#E6E0D4]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RESTAURANTS.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}

          {form.role === ROLES.EMPLOYEE && (
            <UserAreaPicker selected={form.areas} onToggle={toggleArea} />
          )}

          <label className="flex items-center gap-3 bg-white border border-[#E6E0D4] rounded-lg px-3 py-2">
            <Switch checked={!!form.active} onCheckedChange={(v) => set({ active: v })} />
            <span className="text-sm text-[#0B1F3A]">Active</span>
          </label>
        </div>
        <DialogFooter className="pt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-[#0B1F3A] hover:bg-[#11294A] text-white">
            {user ? "Save" : "Create user"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10.5px] tracking-eyebrow uppercase text-[#0B1F3A]/60">{label}</Label>
      {children}
    </div>
  );
}
